import { XIcon } from '@heroicons/react/solid';
import {
  CheckCircleIcon,
  ExclamationIcon,
  InformationCircleIcon,
  SparklesIcon,
} from '@heroicons/react/solid';
import classNames from 'classnames';
import { Avatar } from 'components/atoms/Avatar';
import { Button } from 'components/atoms/Button';
import type { FC } from 'react';
import type {
  ToastButton,
  ToastLinkButton,
  ToastProps,
  ToastVariant,
} from './Toast.types';

// ── New design helpers ───────────────────────────────────────────────

const variantIconMap: Record<ToastVariant, FC<{ className?: string }>> = {
  ai: SparklesIcon,
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationIcon,
  error: InformationCircleIcon,
};

const variantIconColorMap: Record<ToastVariant, string> = {
  ai: 'text-[#278ED3]',
  info: 'text-[#278ED3]',
  success: 'text-green-500',
  warning: 'text-amber-500',
  error: 'text-red-500',
};

const LinkButtons: FC<{ linkButtons: ToastLinkButton[] }> = ({
  linkButtons,
}) => (
  <div className="flex items-center gap-2">
    {linkButtons.map(({ title, onClick, variant = 'primary' }, index) => (
      <>
        {index > 0 && (
          <span className="text-sm text-[#989898] leading-5">&#8729;</span>
        )}
        <button
          key={index}
          type="button"
          onClick={onClick}
          className={classNames(
            'text-sm font-medium leading-5 underline',
            variant === 'primary' ? 'text-[#1B1B1B]' : 'text-[#5F5F5F]',
          )}
        >
          {title}
        </button>
      </>
    ))}
  </div>
);

// ── Legacy design helpers (deprecated – will be removed) ─────────────

const SplitButtons: FC<{ splitButtons: ToastButton[] }> = ({
  splitButtons,
}) => (
  <div
    className={classNames(
      'w-20 h-full',
      'flex flex-col',
      'border-l border-l-grey-400',
      'text-primary',
    )}
  >
    {splitButtons.map(({ title, ...props }, ndx) => (
      <Button
        size="xs"
        key={ndx}
        className={classNames('flex-1', {
          'border-t border-t-grey-400': !!ndx,
        })}
        {...props}
      >
        {title}
      </Button>
    ))}
  </div>
);

const ActionButtons: FC<{ actionButtons: ToastButton[] }> = ({
  actionButtons,
}) => (
  <div className="flex flex-shrink-0 gap-2">
    {actionButtons.map(({ title, ...props }, index) => (
      <Button key={index} size="xs" {...props}>
        {title}
      </Button>
    ))}
  </div>
);

// ── New Toast (variant-based) ────────────────────────────────────────

const NewToast: FC<ToastProps> = ({
  variant,
  title,
  desc,
  linkButtons,
  onClose,
  onClick,
}) => {
  const VariantIcon = variant ? variantIconMap[variant] : null;
  const variantIconColor = variant ? variantIconColorMap[variant] : '';

  return (
    <div
      className={classNames(
        'text-sm',
        'flex flex-1 gap-4 items-start',
        'pt-2.5 pb-3 px-2.5',
        'bg-white backdrop-blur-[6px] rounded-xl overflow-hidden',
        {
          'border border-[#278ED3] border-solid': variant === 'ai',
          'shadow-toast': variant !== 'ai',
        },
      )}
    >
      <div className="flex-1 flex gap-2 items-start min-w-0" onClick={onClick}>
        {VariantIcon && (
          <div className="flex-shrink-0">
            <VariantIcon className={classNames('w-6 h-6', variantIconColor)} />
          </div>
        )}

        <div className="flex flex-1 flex-col gap-1.5 min-w-0">
          <div className="flex flex-col gap-0.5">
            {title && (
              <span className="font-medium text-sm leading-5 text-[#1B1B1B]">
                {title}
              </span>
            )}
            {desc?.map?.((descItem) => (
              <span
                key={descItem}
                className="text-sm font-normal leading-5 text-[#5F5F5F]"
              >
                {descItem}
              </span>
            ))}
          </div>

          {linkButtons && linkButtons.length > 0 && (
            <LinkButtons linkButtons={linkButtons} />
          )}
        </div>
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 p-0.5 rounded-md hover:bg-gray-100 transition-colors"
        >
          <XIcon className="w-5 h-5 text-[#989898]" />
        </button>
      )}
    </div>
  );
};

// ── Legacy Toast (deprecated – will be removed) ──────────────────────

const LegacyToast: FC<ToastProps> = ({
  hasCloseIcon,
  title,
  desc,
  icon,
  avatar,
  splitButtons,
  actionButtons,
  containerClassNames,
  iconWrapperClassNames,
  titleClassNames,
  closeIconWrapperClassNames,
  closeIconClassNames,
  actionButtonsContainerClassNames,
  onClick,
  onClose,
}) => {
  return (
    <div
      className={classNames(
        'text-sm',
        'flex flex-1 gap-4',
        'px-4',
        'bg-white shadow-colored rounded-lg items-center',
        containerClassNames,
      )}
    >
      <div className="flex-1 flex gap-4 items-center" onClick={onClick}>
        {(avatar || icon) && (
          <div className="flex-shrink-0">
            {avatar ? (
              <div className="py-4">
                <Avatar {...avatar} />
              </div>
            ) : null}
            {icon ? (
              <div className={classNames('py-4', iconWrapperClassNames)}>
                {icon}
              </div>
            ) : null}
          </div>
        )}

        <div className="py-4 flex flex-1 flex-col">
          <span
            className={classNames(
              'font-bold text-primary leading-tight',
              titleClassNames,
            )}
          >
            {title}
          </span>
          {desc?.map?.((descItem) => (
            <span key={descItem} className="text-primary-700 leading-tight">
              {descItem}
            </span>
          ))}
        </div>
      </div>

      <div className={actionButtonsContainerClassNames}>
        {actionButtons ? <ActionButtons actionButtons={actionButtons} /> : null}
      </div>

      {(hasCloseIcon || splitButtons) && (
        <div className="flex flex-col items-end">
          {hasCloseIcon ? (
            <div
              className={classNames('p-2', closeIconWrapperClassNames)}
              onClick={onClose}
            >
              <XIcon
                className={classNames(
                  'w-4 h-4 text-gray-500',
                  closeIconClassNames,
                )}
              />
            </div>
          ) : null}
          {splitButtons ? <SplitButtons splitButtons={splitButtons} /> : null}
        </div>
      )}
    </div>
  );
};

// ── Public Toast component ───────────────────────────────────────────

const Toast: FC<ToastProps> = (props) => {
  if (props.variant) {
    return <NewToast {...props} />;
  }

  return <LegacyToast {...props} />;
};

export default Toast;
